import { Tooltip } from "@fluentui/react-components";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Chat24Regular, Add24Regular } from "@fluentui/react-icons";
import { HistoryItem } from "../HistoryItem";
import { IndexedDB } from "../../indexedDB/IndexedDB";
import { HistoryMetaData } from "../../../api/models";
import { Answers } from "../../../api/models";
import { AnswerMode } from "../../answer-modes/AnswerModeContainer/AnswerMode";
import styles from "./HistorySidePanel.module.css";

const HISTORY_COUNT_PER_LOAD = 20;

/* ── Minimal chevron icons for collapse/expand ── */
const CollapseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="11 17 6 12 11 7" />
    <polyline points="18 17 13 12 18 7" />
  </svg>
);

const ExpandIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="13 17 18 12 13 7" />
    <polyline points="6 17 11 12 6 7" />
  </svg>
);

interface HistoryPanelProps {
  notify: number; // incremented when the history changed
  onChatSelected: (
    answers: Answers,
    answerMode: AnswerMode[],
    documents: string[],
    filenames: string[],
    image_filenames: string[],
    error: string | null,
    snapshot: Record<string, any> | null,  // ADD
    selectedSites: string[],
  ) => void;
  onDelete: (
    sessionState: string,
    documents: string[],
    filenames: string[],
    image_filenames: string[],
  ) => void;
  onItemSelected: (id: string) => void;
  sessionState: string;
  disabled: boolean;
  indexedDBInstance: IndexedDB;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onNewChat: () => void;
}

export const HistoryPanel: React.FC<HistoryPanelProps> = ({
  notify,
  onChatSelected,
  onDelete,
  onItemSelected,
  sessionState,
  disabled,
  indexedDBInstance,
  isCollapsed,
  onToggleCollapse,
  onNewChat,
}) => {
  const [history, setHistory] = useState<HistoryMetaData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const loadGenRef = useRef(0);

  const appendUnique = (newItems: HistoryMetaData[]) => {
    setHistory((prev) => {
      const existingIds = new Set(prev.map((x) => x.id));
      const filtered = newItems.filter((x) => !existingIds.has(x.id));
      return [...prev, ...filtered];
    });
  };

  const reloadHistory = useCallback(async () => {
    const myGen = ++loadGenRef.current;

    setIsRefreshing(true);
    setHistory([]);
    setHasMoreHistory(true);
    indexedDBInstance.resetContinuationToken();

    const items = await indexedDBInstance.getNextItems(HISTORY_COUNT_PER_LOAD);

    if (myGen !== loadGenRef.current) return;

    setHistory(items);
    setHasMoreHistory(items.length >= HISTORY_COUNT_PER_LOAD);
    setIsRefreshing(false);
  }, [indexedDBInstance]);

  useEffect(() => {
    if (indexedDBInstance) reloadHistory();
  }, [indexedDBInstance]);

  useEffect(() => {
    reloadHistory();
  }, [notify]);

  const loadMoreHistory = useCallback(async () => {
    if (isLoading || isRefreshing || !hasMoreHistory) return;

    const myGen = loadGenRef.current;
    setIsLoading(true);

    const items = await indexedDBInstance.getNextItems(HISTORY_COUNT_PER_LOAD);

    if (myGen !== loadGenRef.current) {
      setIsLoading(false);
      return;
    }

    appendUnique(items);
    setHasMoreHistory(items.length >= HISTORY_COUNT_PER_LOAD);
    setIsLoading(false);
  }, [isLoading, isRefreshing, hasMoreHistory, indexedDBInstance]);

  const handleSelect = async (id: string) => {
    const { answers, answerMode, error, stateSnapshot, selectedSites } = await indexedDBInstance.getItem(id);
    const result = await indexedDBInstance.getDocs(id);

    if (!answers) return;

    const documents = result?.documents ?? [];
    const filenames = result?.filenames ?? [];
    const image_filenames = result?.image_filenames ?? [];

    onChatSelected(
      answers,
      answerMode,
      documents,
      filenames,
      image_filenames,
      error,
      stateSnapshot,
      selectedSites,

    );

    setSelectedItemId(id);
    onItemSelected(id);
  };

  const handleDelete = async (id: string) => {
    const result = await indexedDBInstance.getDocs(id);

    const documents = result?.documents ?? [];
    const filenames = result?.filenames ?? [];
    const image_filenames = result?.image_filenames ?? [];

    onDelete(id, documents, filenames, image_filenames);

    await indexedDBInstance.deleteItem(id);
    setHistory((prev) => prev.filter((x) => x.id !== id));
  };

  useEffect(() => {
    setSelectedItemId(sessionState);
  }, [sessionState]);

  const groupedHistory = useMemo(() => groupHistory(history), [history]);

  return (
    <div
      className={`${styles.panel} ${isCollapsed ? styles.panelCollapsed : ""}`}
    >
      {/* ── HEADER ── */}
      <div className={styles.header}>
        {!isCollapsed && (
          <span className={styles.headerTitle}>Chat History</span>
        )}
        <Tooltip
          content={isCollapsed ? "Expand panel" : "Collapse panel"}
          positioning="after"
          relationship="label"
        >
          <button
            className={styles.iconButton}
            onClick={onToggleCollapse}
            aria-label={isCollapsed ? "Expand panel" : "Collapse panel"}
          >
            {isCollapsed ? <ExpandIcon /> : <CollapseIcon />}
          </button>
        </Tooltip>
      </div>

      {/* ── SCROLLABLE BODY ── */}
      {!isCollapsed && (
        <div className={styles.body}>
          {Object.entries(groupedHistory).map(([group, items]) => (
            <div key={group} className={styles.group}>
              <p className={styles.groupLabel}>{group}</p>
              {items.map((item) => (
                <HistoryItem
                  key={item.id}
                  item={item}
                  onSelect={handleSelect}
                  onDelete={handleDelete}
                  isSelected={item.id === selectedItemId}
                  disabled={disabled}
                />
              ))}
            </div>
          ))}

          {history.length === 0 && !isRefreshing && (
            <p className={styles.emptyState}>No Chat History</p>
          )}

          {hasMoreHistory && !isLoading && !isRefreshing && (
            <InfiniteLoadingButton func={loadMoreHistory} />
          )}
        </div>
      )}

      {/* ── FOOTER: NEW CHAT BUTTON (always at bottom) ── */}
      <div className={styles.footer}>
        {isCollapsed ? (
          <Tooltip content="New Chat" positioning="after" relationship="label">
            <button
              className={styles.newChatCollapsed}
              onClick={onNewChat}
              disabled={disabled}
              aria-label="New Chat"
            >
              <Add24Regular />
            </button>
          </Tooltip>
        ) : (
          <button
            className={styles.newChatExpanded}
            onClick={onNewChat}
            disabled={disabled}
          >
            <Chat24Regular style={{ flexShrink: 0 }} />
            <span>New Chat</span>
          </button>
        )}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// Grouping function
// ------------------------------------------------------------------
function groupHistory(history: HistoryMetaData[]) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);

  return history.reduce(
    (groups, item) => {
      const itemDate = new Date(item.timestamp);
      let group;

      if (itemDate >= today) {
        group = "Today";
      } else if (itemDate >= yesterday) {
        group = "Yesterday";
      } else if (itemDate >= lastWeek) {
        group = "Last 7 days";
      } else {
        const isCurrentYear = itemDate.getFullYear() === today.getFullYear();
        if (isCurrentYear) {
          group = itemDate.toLocaleDateString(undefined, { month: "long" });
        } else {
          group = itemDate.toLocaleDateString(undefined, {
            month: "long",
            year: "numeric",
          });
        }
      }

      if (!groups[group]) groups[group] = [];
      groups[group].push(item);

      return groups;
    },
    {} as Record<string, HistoryMetaData[]>,
  );
}

interface InfiniteLoadingButtonProps {
  func: () => void;
}

const InfiniteLoadingButton: React.FC<InfiniteLoadingButtonProps> = ({
  func,
}) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const funcRef = useRef(func);

  useEffect(() => {
    funcRef.current = func;
  }, [func]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            funcRef.current?.();
          }
        });
      },
      { rootMargin: "50px" },
    );

    const button = buttonRef.current;
    if (button) observer.observe(button);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <button
      ref={buttonRef}
      onClick={func}
      style={{
        height: "1px",
        width: "100%",
        opacity: 0,
        border: "none",
        pointerEvents: "none",
      }}
    />
  );
};